import React, {
  createContext,
  useState,
  useCallback,
  useRef,
  useMemo,
  Suspense,
  lazy,
} from 'react';
import type { AssetResponse } from '@lp/lib-asset-api';

// Lazy loading defers the import of ImageManager and its dependencies until the dialog is opened.
// This avoids initialization timing issues where the asset API singleton would be created before the
// environment is fully ready, and reduces the initial bundle size with code that users may not use in their session.
const ImageManager = lazy(() =>
  import('@/components/dialogs/ImageManager').then((module) => ({ default: module.ImageManager })),
);

interface OpenImageManagerOptions {
  onSelectImage: (image: AssetResponse) => void;
  activeImageId?: string;
  allowedContentTypes?: string[];
}

interface ImageManagerContextValue {
  isOpen: boolean;
  openImageManager: (options: OpenImageManagerOptions) => void;
  closeImageManager: () => void;
}

export const ImageManagerContext = createContext<ImageManagerContextValue | null>(null);

interface ImageManagerProviderProps {
  children: React.ReactNode;
}

export const ImageManagerProvider: React.FC<ImageManagerProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeImageId, setActiveImageId] = useState<string | undefined>();
  const [allowedContentTypes, setAllowedContentTypes] = useState<string[] | undefined>();
  const onSelectImageRef = useRef<((image: AssetResponse) => void) | null>(null);

  const openImageManager = useCallback((options: OpenImageManagerOptions) => {
    onSelectImageRef.current = options.onSelectImage;
    setActiveImageId(options.activeImageId);
    setAllowedContentTypes(options.allowedContentTypes);
    setIsOpen(true);
  }, []);

  const closeImageManager = useCallback(() => {
    setIsOpen(false);
    setActiveImageId(undefined);
    setAllowedContentTypes(undefined);
    onSelectImageRef.current = null;
  }, []);

  const handleSelectImage = useCallback(
    (image: AssetResponse) => {
      onSelectImageRef.current?.(image);
      closeImageManager();
    },
    [closeImageManager],
  );

  const value = useMemo<ImageManagerContextValue>(
    () => ({
      isOpen,
      openImageManager,
      closeImageManager,
    }),
    [isOpen, openImageManager, closeImageManager],
  );

  return (
    <ImageManagerContext.Provider value={value}>
      {children}
      {isOpen && (
        <Suspense fallback={null}>
          <ImageManager
            isOpen={isOpen}
            onClose={closeImageManager}
            onSelectImage={handleSelectImage}
            activeImageId={activeImageId}
            allowedContentTypes={allowedContentTypes}
          />
        </Suspense>
      )}
    </ImageManagerContext.Provider>
  );
};
